import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import CategoryTag from '@/components/base/CategoryTag';
import SEOMeta from '@/components/base/SEOMeta';
import { categories, categoryHeaders } from '@/mocks/categories';
import { getVideosByCategory, videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'hot');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const headerInfo = categoryHeaders[activeSlug] || categoryHeaders.hot;
  const categoryVideos = getVideosByCategory(activeSlug);

  const otherVideos = videos.filter((v) => v.category !== activeSlug).slice(0, 6);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 黑料在线观看`,
    description: headerInfo.description,
    url: `https://heiliao-online.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料在线观看',
      url: 'https://heiliao-online.com',
    },
  };

  return (
    <>
      <SEOMeta
        title={`${currentCategory.name} - 最新${currentCategory.name}视频免费在线观看 | 黑料在线`}
        description={`黑料在线${currentCategory.name}频道，${headerInfo.description}。最新${currentCategory.name}视频资源，高清流畅免费播放。`}
        keywords={`${currentCategory.name},黑料在线观看,最新${currentCategory.name}视频,免费在线播放,黑料视频,热门${currentCategory.name}`}
        canonicalUrl={`https://heiliao-online.com/category/${activeSlug}`}
        ogImage={headerInfo.coverImage}
        schemaData={[categorySchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Category Header */}
        <section className="relative w-full h-[200px] md:h-[280px] overflow-hidden">
          <img
            src={headerInfo.coverImage}
            alt={headerInfo.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="text-center">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-2">
                {headerInfo.title}
              </h1>
              <p className="text-xs md:text-sm text-background-200 max-w-xl mx-auto">
                {headerInfo.description}
              </p>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 py-4 bg-background-50 sticky top-16 z-40 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <CategoryTag
                  key={cat.id}
                  slug={cat.slug}
                  name={cat.name}
                  icon={cat.icon}
                  active={activeSlug === cat.slug}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Videos Grid */}
        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {categoryVideos.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    thumbnail={video.thumbnail}
                    duration={video.duration}
                    views={video.views}
                    date={video.date}
                    category={video.category}
                    categoryName={currentCategory.name}
                    isHot={video.isHot}
                    variant="large"
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-video-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无视频内容，敬请期待</p>
              </div>
            )}

            {categoryVideos.length > 0 && categoryVideos.length < 8 && (
              <div className="text-center mt-6 text-sm text-foreground-500">
                已加载全部 {categoryVideos.length} 个视频
              </div>
            )}
          </div>
        </section>

        {/* Other Categories */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-5">
              其他分类推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  thumbnail={video.thumbnail}
                  duration={video.duration}
                  views={video.views}
                  date={video.date}
                  category={video.category}
                  categoryName={categories.find((c) => c.slug === video.category)?.name || video.category}
                  variant="compact"
                />
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}